import React from 'react';
import PropTypes from 'prop-types';
import Header from '@ajat/design-system/Header';
import TopBar from '@ajat/design-system/TopBar';
import CartIcon from '@ajat/design-system/CartIcon';
import { ComponentPage, DemoCard, ViewportFrame } from '../shared';
import { DEMO_NAV_ITEMS, DEMO_MOBILE_PROMO, DEMO_SIDE_MENU } from './demoData';

export const meta = {
  component: Header,
  title: 'Header',
  group: 'Navigation',
  importPath: '@ajat/design-system/Header',
  figmaNode: '3602:1212',
  description:
    'One responsive component, not two. Figma\'s Resolution variant is handled entirely by CSS media queries — the JSX renders both the desktop nav and the mobile hamburger, and the breakpoint decides which is visible. There is deliberately no `resolution` prop. CLS has three desktop states, not one: see the breakpoints card below.',
  props: [
    ['brand', "'CLS' | 'ABC' | 'Overall'", "'CLS'", 'Brand — picks the logo, the desktop layout and the default right-hand buttons (CLS centres the logo, ABC left-aligns it; Overall centres it with the hamburger kept on desktop next to the nav, a gray-075 bar and wishlist + account + cart instead of the school button)'],
    ['subBrand', "'main' | 'clothing'", "'main'", 'Sub-brand logo variant'],
    ['mode', "'default' | 'checkout'", "'default'", 'Checkout mode replaces the nav with a back button and a secure-checkout label'],
    ['showTopBar', 'bool', 'false', 'Render a TopBar above the header row'],
    ['topBarNotice', 'string', '—', 'Notice text passed through to the TopBar'],
    ['topBarLinks', 'array', '—', 'Links passed through to the TopBar'],
    ['hideOnScroll', 'bool', 'false', 'Hide the header while scrolling down'],
    ['cartCount', 'number', '—', 'Badge count on the cart icon'],
    ['cartItems', 'array', '—', 'Minicart line items — the popup only opens when this is non-empty'],
    ['cartTotal', 'string', '—', 'Minicart total'],
    ['cartItemsLabel', 'string', '—', 'Minicart header label'],
    ['cartTotalLabel', 'string', "'Total'", 'Minicart total label'],
    ['cartCtaLabel', 'string', '—', 'Minicart CTA label'],
    ['onCartCtaClick', 'func', '—', 'Minicart CTA handler; also fires when the cart icon is clicked with an empty cart'],
    ['menuIcon', 'node', 'brand', 'Hamburger icon (lucide Menu; Overall passes its custom bars by default)'],
    ['menuOnDesktop', 'bool', 'brand', 'Keep the hamburger and the built-in drawer on desktop (Overall on by default)'],
    ['logoSrc', 'string', 'brand', 'Logo image URL — overrides the brand logotype (e.g. from a CMS)'],
    ['logoHref', 'string', '—', 'Logo link target'],
    ['onLogoClick', 'func', '—', 'Logo click handler'],
    ['labels', 'object', 'English', '{ cart, items, close, checkout, menu, back, closeMenu, nav, mobileNav, logo } — the header\'s own strings (aria-labels, minicart fallbacks); brands pass their language'],
    ['sideMenu', '{title?, closeLabel?, featured?, cards?, links?, actions?, actionsOnDesktop?}', '—', 'DS SideMenu content — when set, the hamburger opens the SideMenu instead of the built-in drawer (Overall). The Header owns its open state and closes it on routeKey changes; title / closeLabel default to labels.menu / labels.closeMenu'],
    ['onMenuClick', 'func', '—', 'Hamburger handler; when set it replaces the built-in drawer and sideMenu (the host opens its own menu)'],
    ['onBackClick', 'func', '—', 'Back handler in checkout mode'],
    ['checkoutLabel', 'string', "'Secure Checkout'", 'Right-hand label in checkout mode'],
    ['backLabel', 'string', "'Back'", 'Back button label in checkout mode'],
    ['navItems', 'array', 'auto', '[{ label, href, submenu? }] — a submenu opens the desktop mega-menu'],
    ['routeKey', 'string', '—', 'Router location key; a change means the customer navigated and closes any open menu'],
    ['mobileSettings', 'object', '—', 'Mobile drawer configuration, including the promo card'],
    ['schoolLabel', 'string', 'brand', 'School button label (desktop header + mobile drawer footer) — pass the selected school\'s name; falls back to the brand\'s "choose school" copy'],
    ['onSchoolClick', 'func', '—', 'Opens the school selector'],
    ['showSchoolButton', 'bool', 'brand', 'Show the school button — gate this on the brand\'s showSchoolSelector flag (default: on, Overall off)'],
    ['showWishlistButton', 'bool', 'brand', 'Show the wishlist (heart) button on desktop (default: Overall on, others off)'],
    ['onWishlistClick', 'func', '—', 'Wishlist button handler'],
    ['wishlistLabel', 'string', "'Wishlist'", 'Accessible label of the wishlist button'],
    ['showUserButton', 'bool', 'brand', 'Show the account button (default: Overall on, others off)'],
    ['onUserClick', 'func', '—', 'Account button handler'],
    ['showCartButton', 'bool', 'true', 'Show the cart button + minicart (desktop + mobile) — false for sites without a shop (ABC DK editorial site)'],
    ['orderLabel', 'string', "'Edit order'", 'Edit-order link label'],
    ['className', 'string', "''", 'Additional CSS class'],
  ],
  propsNote:
    'Brand pages never use this component directly — they import their own wrapper from Clothing/{Brand}/Common, which supplies the cart wiring and the school selector.',
  breakpointNote: true,
  usage: [
    {
      title: 'On a brand page',
      code: `// Always the brand wrapper, never the DS component directly —
// the wrapper carries cart wiring and the school selector.
import { Header, Footer } from '../Common';

<Header />                                   // defaults per brand
<Header showTopBar={false} />                // override where needed`,
    },
    {
      title: 'Checkout mode',
      code: `<Header
  brand="CLS"
  mode="checkout"
  onBackClick={() => navigate('/clothing/cls-dk/shopping-cart')}
/>`,
    },
    {
      title: 'Nav items with a mega-menu',
      code: `const navItems = [
  {
    label: 'STUDIETURTRØJE',
    href: '#',
    submenu: {
      watermark: 'Studieturtrøje',
      columns: [
        { type: 'card', cardType: 'button', image: hero, label: 'BESTIL', href: '#' },
        { type: 'links', items: [{ label: 'FAQ', href: '/faq' }] },
      ],
    },
  },
  { label: 'STUDENTERHUE', href: '#' },   // no submenu — plain link
];`,
    },
  ],
};

export default function HeaderPage({ brand = 'CLS' }) {
  // Brands without their own demo content get CLS's (Overall has nav items, not a promo).
  const navItems = DEMO_NAV_ITEMS[brand] ?? DEMO_NAV_ITEMS.CLS;
  const mobilePromo = DEMO_MOBILE_PROMO[brand] ?? DEMO_MOBILE_PROMO.CLS;
  // Overall: the hamburger opens the DS SideMenu (over the dashboard, not inside the frame)
  const sideMenu = DEMO_SIDE_MENU[brand];

  const full = (
    <>
      <TopBar brand={brand} />
      <Header
        brand={brand}
        mode="default"
        navItems={navItems}
        mobilePromo={mobilePromo}
        sideMenu={sideMenu}
        schoolLabel="Nørre Gymnasium"
        showUserButton
        orderLabel="Edit order"
      />
    </>
  );

  return (
    <ComponentPage meta={meta}>
      <DemoCard
        title="Breakpoints"
        hint="the previews below each run in their own viewport"
        surface={false}
      >
        <p className="typo-body-sm" style={{ color: 'var(--color-gray-7)', maxWidth: '68ch' }}>
          This component switches layout on <code style={{ color: 'var(--color-gray-10)' }}>@media</code>,
          which reads the <strong>viewport</strong> — not the width of the box it sits in. Every preview on this
          page is therefore rendered inside an iframe at the stated width, then scaled to fit; that is the only
          way the layout you see here matches a real page. CLS has three desktop states:
        </p>
        <ul
          className="typo-body-sm"
          style={{ color: 'var(--color-gray-7)', maxWidth: '68ch', paddingTop: 'var(--space-ui-medium-s)', paddingLeft: '1.2em', listStyle: 'disc' }}
        >
          <li style={{ paddingBottom: 'var(--space-ui-small-l)' }}>
            <strong>≥ 1360px</strong> — nav left, logo absolutely centred, school and cart right.
          </li>
          <li style={{ paddingBottom: 'var(--space-ui-small-l)' }}>
            <strong>1024–1359px</strong> — the centred logo would collide with the nav as it grows, so the logo
            moves into the flow before the nav (the ABC arrangement). Deliberate; see Header.css.
          </li>
          <li>
            <strong>&lt; 1024px</strong> — hamburger left, logo centred, cart right; the nav becomes a drawer.
          </li>
        </ul>
      </DemoCard>

      <DemoCard title="Desktop — wide" hint="≥ 1360px · nav left, logo centred" flush>
        <ViewportFrame width={1440} height={116} label="Header at 1440px">{full}</ViewportFrame>
      </DemoCard>

      <DemoCard title="Desktop — narrow" hint="1024–1359px · logo moves before the nav" flush>
        <ViewportFrame width={1200} height={116} label="Header at 1200px">{full}</ViewportFrame>
      </DemoCard>

      <DemoCard
        title="Mobile"
        hint={sideMenu ? '< 1024px · the hamburger opens the DS SideMenu' : '< 1024px · hamburger and drawer'}
        flush
      >
        <ViewportFrame width={393} height={116} label="Header at 393px">{full}</ViewportFrame>
      </DemoCard>

      <DemoCard title="Header only" hint="no submenu — falls back to simple nav" flush>
        <ViewportFrame width={1440} height={68} label="Header without submenu at 1440px">
          <Header brand={brand} mode="default" />
        </ViewportFrame>
      </DemoCard>

      <DemoCard title="Editorial site — no shop" hint="showCartButton={false} · showSchoolButton={false}" flush>
        <ViewportFrame width={1440} height={68} label="Header without cart at 1440px">
          <Header brand={brand} mode="default" navItems={navItems} showCartButton={false} showSchoolButton={false} />
        </ViewportFrame>
      </DemoCard>

      <DemoCard title="Checkout mode" hint="back button, centred logo, secure label" flush>
        <ViewportFrame width={1440} height={68} label="Checkout header at 1440px">
          <Header brand={brand} mode="checkout" />
        </ViewportFrame>
      </DemoCard>

      <DemoCard title="Checkout mode — mobile" flush>
        <ViewportFrame width={393} height={68} label="Checkout header at 393px">
          <Header brand={brand} mode="checkout" />
        </ViewportFrame>
      </DemoCard>
      <DemoCard title="CartIcon" hint="@ajat/design-system/CartIcon · the header bag; plus = the add-to-cart CTA">
        <div style={{ display: 'flex', gap: 'var(--space-ui-medium-m)', alignItems: 'center', color: 'var(--color-gray-10)' }}>
          <CartIcon />
          <CartIcon plus />
        </div>
      </DemoCard>
    </ComponentPage>
  );
}

HeaderPage.propTypes = {
  brand: PropTypes.oneOf(['CLS', 'ABC', 'Overall']),
};
